/**
 * TerminalTabs Component
 *
 * Tab strip for managing multiple terminal sessions.
 * Supports two terminal types: Home (standard) and AI (with database context).
 */

import { useState } from 'react';
import { Home, Bot, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useTerminalStore } from '../store/terminalStore';
import { useAiLockStore } from '../store/aiLockStore';
import { closeTerminalSession } from '../services/terminalApi';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import type { TerminalSession, TerminalType } from '../../../types/terminal';

interface TerminalTabsProps {
  /** Callback to create a new terminal */
  onCreateTerminal: (type: TerminalType) => void;
  /** Whether AI terminal button is disabled (requires connection) */
  aiDisabled?: boolean;
  /** Tooltip for disabled AI button */
  aiDisabledTooltip?: string;
  /** Whether AI workspace sync is available (Pro mode) */
  canUseAiWorkspace?: boolean;
}

/**
 * Individual tab component
 */
function TerminalTab({
  session,
  isActive,
  onSelect,
  onClose,
  onRename,
}: {
  session: TerminalSession;
  isActive: boolean;
  onSelect: () => void;
  onClose: () => void;
  onRename: (newTitle: string) => void;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(session.title);

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditTitle(session.title);
    setIsEditing(true);
  };

  const handleRenameSubmit = () => {
    const trimmed = editTitle.trim();
    if (trimmed && trimmed !== session.title) {
      onRename(trimmed);
    }
    setIsEditing(false);
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRenameSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsEditing(false);
      setEditTitle(session.title);
    }
  };

  // Icon based on terminal type
  const TypeIcon = session.type === 'ai' ? Bot : Home;

  return (
    <div
      className={cn(
        'flex items-center gap-1.5 px-3 py-1 cursor-pointer border-r border-border',
        'hover:bg-muted/50 transition-colors',
        isActive && 'bg-muted border-b-2 border-b-primary'
      )}
      onClick={onSelect}
      onDoubleClick={handleDoubleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={isEditing ? undefined : session.title}
      data-testid={`terminal-session-tab-${session.id}`}
    >
      <TypeIcon className={cn('h-3 w-3 flex-shrink-0', session.type === 'ai' && 'text-purple-500')} />
      {isEditing ? (
        <input
          type="text"
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          onBlur={handleRenameSubmit}
          onKeyDown={handleRenameKeyDown}
          className="text-xs bg-background border border-border rounded px-1 w-[100px] focus:outline-none focus:ring-1 focus:ring-primary"
          autoFocus
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span className="text-xs truncate max-w-[120px] select-text">{session.title}</span>
      )}
      <button
        className={cn(
          'p-0.5 rounded hover:bg-muted-foreground/20 transition-opacity',
          isHovered || isActive ? 'opacity-100' : 'opacity-0'
        )}
        onClick={handleClose}
        title="Close terminal"
        data-testid={`terminal-session-close-${session.id}`}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

/**
 * Terminal tabs strip component
 */
export function TerminalTabs({
  onCreateTerminal,
  aiDisabled = false,
  aiDisabledTooltip,
  canUseAiWorkspace = true,
}: TerminalTabsProps) {
  const sessions = useTerminalStore((s) => s.sessions);
  const activeSessionId = useTerminalStore((s) => s.activeSessionId);
  const setActiveSession = useTerminalStore((s) => s.setActiveSession);
  const removeSession = useTerminalStore((s) => s.removeSession);
  const renameSession = useTerminalStore((s) => s.renameSession);
  const removeSessionLocks = useAiLockStore((s) => s.removeSessionLocks);

  // State for lock confirmation dialog
  const [confirmSession, setConfirmSession] = useState<TerminalSession | null>(null);

  const handleCloseSession = async (session: TerminalSession) => {
    // Check if this AI session has any active locks
    if (session.type === 'ai') {
      const lockedTabs = useAiLockStore.getState().lockedTabs;
      const hasLock = Array.from(lockedTabs.values()).some((lock) => lock.sessionId === session.id);

      if (hasLock) {
        // Show confirmation dialog
        setConfirmSession(session);
        return;
      }
    }

    // No lock, close directly
    await doCloseSession(session);
  };

  const doCloseSession = async (session: TerminalSession) => {
    // Close the PTY session
    try {
      await closeTerminalSession({ sessionId: session.id });
    } catch (err) {
      console.error('[TerminalTabs] Error closing session:', err);
    }
    // Remove locks for this session
    if (session.type === 'ai') {
      removeSessionLocks(session.id);
    }
    // Remove from store (will also update if PTY exit event fires)
    removeSession(session.id);
  };

  const handleApplyAndClose = async () => {
    if (!confirmSession) return;
    // The lock release event will handle syncing the content back to the tab
    // Just close the session normally - the lock will be released and working.sql
    // content will be synced to the tab (already handled in syncManager)
    await doCloseSession(confirmSession);
    setConfirmSession(null);
  };

  const handleDiscardAndClose = async () => {
    if (!confirmSession) return;
    // Update local store to release locks (per-tab meta lock system manages file cleanup)
    removeSessionLocks(confirmSession.id);
    // Now close the session
    await doCloseSession(confirmSession);
    setConfirmSession(null);
  };

  const handleCancelClose = () => {
    setConfirmSession(null);
  };

  return (
    <>
      {/* Lock confirmation dialog */}
      <AlertDialog open={!!confirmSession} onOpenChange={(open) => !open && setConfirmSession(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>AI has unsaved work</AlertDialogTitle>
            <AlertDialogDescription>
              The AI agent has edited this tab but hasn&apos;t finished. What would you like to do?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-2 sm:flex-row">
            <AlertDialogCancel onClick={handleCancelClose}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDiscardAndClose}
              className="bg-destructive-fill text-destructive-foreground hover:bg-destructive-fill/90"
            >
              Discard
            </AlertDialogAction>
            <AlertDialogAction onClick={handleApplyAndClose}>
              Apply changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tabs content */}
      <div className="flex items-start border-b border-border bg-muted/30 min-w-0 flex-1 select-none">
        {/* Terminal creation buttons - always on the left */}
        <div className="flex items-center gap-0.5 px-1 flex-shrink-0">
          {/* Home terminal button - always available */}
          <button
            className="flex items-center gap-1 px-2 py-1 hover:bg-muted/50 transition-colors rounded text-xs"
            onClick={() => onCreateTerminal('home')}
            title="New terminal in home directory"
            data-testid="terminal-home-button"
          >
            <Home className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Home</span>
          </button>

          {/* AI Context terminal button */}
	          <button
	            className={cn(
	              'flex items-center gap-1 px-2 py-1 hover:bg-muted/50 transition-colors rounded text-xs',
	              aiDisabled && 'opacity-50 cursor-not-allowed'
	            )}
	            onClick={aiDisabled ? undefined : () => onCreateTerminal('ai')}
	            title={
	              aiDisabled
	                ? aiDisabledTooltip
	                : canUseAiWorkspace
	                  ? 'Open AI terminal with database context (with sync)'
	                  : 'Open AI terminal (workspace sync requires Pro)'
	            }
	            disabled={aiDisabled}
	            data-testid="terminal-ai-button"
	          >
            <Bot className={cn('h-3.5 w-3.5', !aiDisabled && 'text-purple-500')} />
            <span className="hidden sm:inline">AI Context</span>
          </button>
        </div>

	        {/* Session tabs - wrap when space is insufficient */}
		        <div className="flex flex-wrap items-center overflow-x-auto min-w-0" data-testid="terminal-tabs-container">
		          {sessions.map((session) => (
		            <TerminalTab
		              key={session.id}
		              session={session}
		              isActive={session.id === activeSessionId}
              onSelect={() => setActiveSession(session.id)}
              onClose={() => handleCloseSession(session)}
              onRename={(newTitle) => renameSession(session.id, newTitle)}
            />
          ))}
        </div>
      </div>
    </>
  );
}
